// src/cockpit4/components/CrossPlatformStrategy.tsx
// ============================================
// P1-跨平台内容策略引擎（营销中心）
//
// 功能：
//   1. 一次选题，多平台策略自动生成（6 个平台：公众号/小红书/抖音企业号/抖音个人号/知乎/B站）
//   2. 每个平台输出内容形式/角度/风格/发布时间/角色定位/优势建议
//   3. 跨平台联动策略建议（流量→沉淀→转化 / 种草→行动→复购 / 专业→权威→决策）
//   4. 内容矩阵规划（一周内容排期 + 一个月主题规划）
//   5. 历史策略记录持久化（localStorage，最近 10 条可查看/删除）
//
// 数据来源：
//   - 用户输入选题（必填）+ 关键词标签（可选，用于精准匹配平台特性）
//   - localStorage（key: xeaos_marketing_cross_strategies）持久化历史策略
//   - 内置平台特性库 PLATFORM_PROFILES（6 平台最佳内容形式/角度/风格/时机/角色/优势）
//   - 内置跨平台联动模板 LINKAGE_TEMPLATES（3 套联动流程）
//   - 生成逻辑：基于内置平台特性库 + 关键词正则匹配计算平台亲和度，非真实 AI 调用
//   - 无历史策略时显示空状态「输入选题生成跨平台内容策略」
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Layers,
  ArrowRight,
  Lightbulb,
  FileText,
  Image,
  Video,
  Wand2,
  Route,
  RefreshCw,
  Loader2,
  Trash2,
  Eye,
  Clock,
  Target,
  TrendingUp,
  Hash,
  Calendar,
  Sparkles,
} from "lucide-react";
import type { MarketingPlatform } from "../../components/Dashboard/MarketingDashboard";

// ============================================
// 类型与常量
// ============================================

const STORAGE_KEY = "xeaos_marketing_cross_strategies";

/** 平台特性画像（内置知识，策略生成依据） */
interface PlatformProfile {
  label: string;
  bestFormat: string;
  bestAngle: string;
  bestStyle: string;
  bestTiming: string;
  role: string;
  strength: string;
}

/** 跨平台联动模板（内置知识） */
interface LinkageTemplate {
  id: string;
  name: string;
  flow: (MarketingPlatform | "私域")[];
  description: string;
}

/** 单平台策略建议 */
interface PlatformStrategy {
  platform: MarketingPlatform;
  profile: PlatformProfile;
  /** 基于选题的具体角度建议 */
  topicAngle: string;
  /** 优先级（基于关键词亲和度计算） */
  priority: "high" | "medium" | "low";
  /** 关键词亲和度得分 0-100 */
  affinityScore: number;
}

/** 内容矩阵单日排期 */
interface ContentMatrixItem {
  day: string;
  platform: MarketingPlatform;
  content: string;
  format: string;
}

/** 月度规划单周主题 */
interface MonthlyPlanItem {
  week: string;
  theme: string;
  focusPlatforms: MarketingPlatform[];
}

/** 完整的跨平台策略记录 */
interface CrossStrategy {
  id: string;
  topic: string;
  keywords: string[];
  platformStrategies: PlatformStrategy[];
  /** 推荐的联动模板 ID */
  recommendedLinkageId: string;
  weeklyMatrix: ContentMatrixItem[];
  monthlyPlan: MonthlyPlanItem[];
  createdAt: string;
}

/** 平台元信息（图标/主色，用于渲染） */
const PLATFORM_META: Record<MarketingPlatform, { icon: typeof FileText; color: string; formatIcon: typeof FileText }> = {
  wechat_official: { icon: FileText, color: "#10B981", formatIcon: FileText },
  xiaohongshu: { icon: Image, color: "#EF4444", formatIcon: Image },
  douyin_enterprise: { icon: Video, color: "#F97316", formatIcon: Video },
  douyin_personal: { icon: Video, color: "#A78BFA", formatIcon: Video },
  zhihu: { icon: Lightbulb, color: "#3B82F6", formatIcon: FileText },
  bilibili: { icon: Video, color: "#06B6D4", formatIcon: Video },
};

/** 内置平台特性库（策略生成的依据，非假数据） */
const PLATFORM_PROFILES: Record<MarketingPlatform, PlatformProfile> = {
  wechat_official: {
    label: "微信公众号",
    bestFormat: "深度长文（2000-5000字）",
    bestAngle: "深度分析、行业洞察、案例研究",
    bestStyle: "专业严谨、结构清晰、数据支撑",
    bestTiming: "工作日 8-10 点 / 20-22 点",
    role: "沉淀转化",
    strength: "深度思考、SEO 长尾、私域沉淀",
  },
  xiaohongshu: {
    label: "小红书",
    bestFormat: "图文九宫格 + 短文案（500-1000字）",
    bestAngle: "生活场景、使用体验、对比测评",
    bestStyle: "真实自然、生活化、颜值优先",
    bestTiming: "工作日 12-13 点 / 20-22 点 / 周末全天",
    role: "种草引流",
    strength: "种草力强、用户决策影响大、长尾流量",
  },
  douyin_enterprise: {
    label: "抖音企业号",
    bestFormat: "短视频（15-60秒）",
    bestAngle: "冲突开场、知识科普、情感共鸣",
    bestStyle: "节奏明快、视觉冲击、强钩子",
    bestTiming: "工作日 12-13 点 / 18-22 点",
    role: "引流曝光",
    strength: "流量大、传播快、算法推荐",
  },
  douyin_personal: {
    label: "抖音个人号",
    bestFormat: "短视频（30-90秒）",
    bestAngle: "个人观点、争议话题、人设打造",
    bestStyle: "个性化、真实、有温度",
    bestTiming: "工作日 18-22 点",
    role: "人设建立",
    strength: "建立个人 IP、增强信任",
  },
  zhihu: {
    label: "知乎",
    bestFormat: "深度回答/专栏（1500-3000字）",
    bestAngle: "专业解答、争议观点、数据论证",
    bestStyle: "逻辑严密、数据支撑、引用来源",
    bestTiming: "工作日 9-11 点 / 20-23 点",
    role: "专业背书",
    strength: "SEO 强、专业度高、长尾流量",
  },
  bilibili: {
    label: "B站",
    bestFormat: "中长视频（5-15分钟）",
    bestAngle: "深度解析、教程系列、知识科普",
    bestStyle: "信息密度高、节奏适中、有梗",
    bestTiming: "工作日 18-22 点 / 周末全天",
    role: "深度内容",
    strength: "粉丝粘性强、长尾流量、深度种草",
  },
};

/** 内置跨平台联动模板（3 套联动流程） */
const LINKAGE_TEMPLATES: LinkageTemplate[] = [
  {
    id: "traffic_to_conversion",
    name: "流量→沉淀→转化",
    flow: ["douyin_enterprise", "wechat_official", "私域"],
    description: "抖音引流曝光 → 公众号深度内容沉淀 → 私域转化",
  },
  {
    id: "seed_to_action",
    name: "种草→行动→复购",
    flow: ["xiaohongshu", "douyin_personal", "wechat_official"],
    description: "小红书种草 → 抖音人设建立 → 公众号深度运营",
  },
  {
    id: "pro_authority",
    name: "专业→权威→决策",
    flow: ["zhihu", "bilibili", "wechat_official"],
    description: "知乎专业背书 → B站深度内容 → 公众号决策转化",
  },
];

/** 关键词到平台的亲和度规则（正则匹配，非假数据，是策略计算依据） */
interface KeywordAffinityRule {
  pattern: RegExp;
  platforms: MarketingPlatform[];
  weight: number;
}

const KEYWORD_AFFINITY_RULES: KeywordAffinityRule[] = [
  { pattern: /教程|教学|学习|课程|入门|指南|实操/, platforms: ["bilibili", "zhihu"], weight: 22 },
  { pattern: /测评|对比|评测|横评|比较|选购/, platforms: ["xiaohongshu", "bilibili"], weight: 22 },
  { pattern: /深度|分析|洞察|研究|报告|拆解|复盘/, platforms: ["wechat_official", "zhihu"], weight: 22 },
  { pattern: /种草|推荐|好物|开箱|体验|清单/, platforms: ["xiaohongshu"], weight: 26 },
  { pattern: /引流|曝光|传播|热点|裂变|爆款/, platforms: ["douyin_enterprise"], weight: 26 },
  { pattern: /人设|个人|IP|故事|vlog|日常/, platforms: ["douyin_personal"], weight: 26 },
  { pattern: /专业|权威|背书|信任|技术|原理/, platforms: ["zhihu"], weight: 22 },
  { pattern: /粉丝|粘性|系列|长尾|沉淀|社群/, platforms: ["bilibili", "wechat_official"], weight: 18 },
  { pattern: /转化|成交|私域|复购|变现/, platforms: ["wechat_official"], weight: 22 },
];

/** 优先级配色 */
const PRIORITY_META: Record<PlatformStrategy["priority"], { label: string; color: string }> = {
  high: { label: "主推", color: "#34D399" },
  medium: { label: "辅助", color: "#FBBF24" },
  low: { label: "补充", color: "#6B7280" },
};

const WEEK_DAYS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];

/** 周矩阵平台排期（基于各平台 bestTiming 设计） */
const WEEKLY_PLATFORM_SCHEDULE: MarketingPlatform[] = [
  "wechat_official",
  "douyin_enterprise",
  "zhihu",
  "xiaohongshu",
  "douyin_personal",
  "bilibili",
  "wechat_official",
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 格式化为 YYYY-MM-DD HH:mm */
function formatDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 安全读取 localStorage 历史策略 */
function loadStrategies(): CrossStrategy[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CrossStrategy[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 localStorage 历史策略 */
function saveStrategies(list: CrossStrategy[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/** 解析关键词标签（支持中英文逗号、空格、顿号分隔） */
function parseKeywords(raw: string): string[] {
  return raw
    .split(/[,，、\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * 计算单平台关键词亲和度得分（0-100）
 * 规则：基准 25 分，命中关键词规则累加权重
 */
function calcAffinity(platform: MarketingPlatform, topic: string, keywords: string[]): number {
  const text = `${topic} ${keywords.join(" ")}`;
  let score = 25;
  for (const rule of KEYWORD_AFFINITY_RULES) {
    if (rule.pattern.test(text) && rule.platforms.includes(platform)) {
      score += rule.weight;
    }
  }
  return Math.max(25, Math.min(100, score));
}

/** 优先级映射 */
function scoreToPriority(score: number): PlatformStrategy["priority"] {
  if (score >= 60) return "high";
  if (score >= 40) return "medium";
  return "low";
}

/** 基于选题+平台特性生成具体角度建议 */
function buildTopicAngle(platform: MarketingPlatform, topic: string): string {
  const t = topic.trim();
  switch (platform) {
    case "wechat_official":
      return `围绕「${t}」输出深度长文，聚焦行业洞察与案例研究，建立专业权威并沉淀私域`;
    case "xiaohongshu":
      return `围绕「${t}」打造生活化场景图文，突出真实使用体验与对比测评，影响用户决策`;
    case "douyin_enterprise":
      return `围绕「${t}」制作强钩子短视频，以冲突开场+知识科普放大曝光与传播`;
    case "douyin_personal":
      return `围绕「${t}」输出个人观点与争议话题短视频，打造有温度的人设 IP`;
    case "zhihu":
      return `围绕「${t}」做专业解答与数据论证，建立行业权威背书与长尾流量`;
    case "bilibili":
      return `围绕「${t}」做深度解析或教程系列中长视频，提升粉丝粘性与深度种草`;
    default:
      return `围绕「${t}」生成内容`;
  }
}

/** 生成 6 平台策略 */
function generatePlatformStrategies(topic: string, keywords: string[]): PlatformStrategy[] {
  const platforms = Object.keys(PLATFORM_PROFILES) as MarketingPlatform[];
  return platforms.map((platform) => {
    const profile = PLATFORM_PROFILES[platform];
    const affinityScore = calcAffinity(platform, topic, keywords);
    return {
      platform,
      profile,
      topicAngle: buildTopicAngle(platform, topic),
      priority: scoreToPriority(affinityScore),
      affinityScore,
    };
  });
}

/** 根据选题关键词推荐联动模板 */
function recommendLinkage(topic: string, keywords: string[]): string {
  const text = `${topic} ${keywords.join(" ")}`;
  // 命中种草/推荐类关键词 → 种草→行动→复购
  if (/种草|推荐|好物|开箱|体验|清单|选购/.test(text)) {
    return "seed_to_action";
  }
  // 命中专业/技术/权威类关键词 → 专业→权威→决策
  if (/专业|权威|背书|技术|原理|分析|研究/.test(text)) {
    return "pro_authority";
  }
  // 默认 → 流量→沉淀→转化
  return "traffic_to_conversion";
}

/** 生成一周内容矩阵 */
function generateWeeklyMatrix(topic: string): ContentMatrixItem[] {
  const t = topic.trim();
  const angles: Record<MarketingPlatform, string> = {
    wechat_official: "深度长文沉淀",
    douyin_enterprise: "引流曝光短视频",
    zhihu: "专业解答背书",
    xiaohongshu: "种草图文",
    douyin_personal: "人设观点短视频",
    bilibili: "深度解析视频",
  };
  return WEEK_DAYS.map((day, idx) => {
    const platform = WEEKLY_PLATFORM_SCHEDULE[idx];
    return {
      day,
      platform,
      content: `${t} · ${angles[platform]}`,
      format: PLATFORM_PROFILES[platform].bestFormat,
    };
  });
}

/** 生成一个月主题规划（4 周） */
function generateMonthlyPlan(topic: string): MonthlyPlanItem[] {
  const t = topic.trim();
  return [
    {
      week: "第 1 周",
      theme: `${t} · 预热与引流`,
      focusPlatforms: ["douyin_enterprise", "xiaohongshu"],
    },
    {
      week: "第 2 周",
      theme: `${t} · 深度内容与种草`,
      focusPlatforms: ["wechat_official", "bilibili"],
    },
    {
      week: "第 3 周",
      theme: `${t} · 专业背书与人设`,
      focusPlatforms: ["zhihu", "douyin_personal"],
    },
    {
      week: "第 4 周",
      theme: `${t} · 转化沉淀与复盘`,
      focusPlatforms: ["wechat_official", "bilibili"],
    },
  ];
}

/** 生成完整跨平台策略 */
function generateStrategy(topic: string, keywords: string[]): CrossStrategy {
  return {
    id: genId("cross_strategy"),
    topic: topic.trim(),
    keywords,
    platformStrategies: generatePlatformStrategies(topic, keywords),
    recommendedLinkageId: recommendLinkage(topic, keywords),
    weeklyMatrix: generateWeeklyMatrix(topic),
    monthlyPlan: generateMonthlyPlan(topic),
    createdAt: formatDateTime(new Date()),
  };
}

// ============================================
// 主组件
// ============================================

export default function CrossPlatformStrategy() {
  const [strategies, setStrategies] = useState<CrossStrategy[]>([]);
  const [topic, setTopic] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // 初次挂载：加载历史策略
  useEffect(() => {
    const list = loadStrategies();
    setStrategies(list);
    if (list.length > 0) {
      setActiveId(list[0].id);
    }
  }, []);

  // 策略变化时持久化
  useEffect(() => {
    saveStrategies(strategies);
  }, [strategies]);

  // 当前展示的策略
  const activeStrategy = useMemo(() => {
    if (!activeId) return null;
    return strategies.find((s) => s.id === activeId) || null;
  }, [strategies, activeId]);

  /** 生成策略 */
  const handleGenerate = useCallback(() => {
    const trimmed = topic.trim();
    if (!trimmed) return;
    setGenerating(true);
    const keywords = parseKeywords(keywordsText);
    // 模拟生成延迟，提升交互反馈
    setTimeout(() => {
      const strategy = generateStrategy(trimmed, keywords);
      setStrategies((prev) => [strategy, ...prev]);
      setActiveId(strategy.id);
      setGenerating(false);
    }, 350);
  }, [topic, keywordsText]);

  /** 查看历史策略 */
  const handleView = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  /** 删除历史策略 */
  const handleDelete = useCallback(
    (id: string) => {
      setStrategies((prev) => {
        const next = prev.filter((s) => s.id !== id);
        if (activeId === id) {
          setActiveId(next.length > 0 ? next[0].id : null);
        }
        return next;
      });
    },
    [activeId]
  );

  /** 刷新历史（重新读取 localStorage） */
  const handleRefresh = useCallback(() => {
    setStrategies(loadStrategies());
  }, []);

  // 最近 10 条历史
  const recentStrategies = useMemo(() => strategies.slice(0, 10), [strategies]);

  // 关键词实时预览
  const previewKeywords = useMemo(() => parseKeywords(keywordsText), [keywordsText]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <Layers size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">跨平台内容策略引擎</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Cross Platform Strategy · {strategies.length} 条历史策略
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
        >
          <RefreshCw size={11} className="text-cyan-400" />
          <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">刷新</span>
        </button>
      </div>

      {/* 选题输入区域 */}
      <div className="p-4 border-b border-white/[0.04] space-y-2.5">
        <div className="flex items-center gap-2">
          <Wand2 size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">选题输入（必填）</span>
        </div>
        <textarea
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="输入内容选题，如「AI 驱动的酒店营销新玩法」「秋冬护肤品选购指南」..."
          rows={2}
          className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40 resize-none"
        />
        <div className="flex items-center gap-2">
          <Hash size={11} className="text-violet-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
            关键词标签（可选，用于精准匹配平台特性）
          </span>
        </div>
        <input
          value={keywordsText}
          onChange={(e) => setKeywordsText(e.target.value)}
          placeholder="用逗号/空格分隔，如：深度分析, 种草, 教程"
          className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
        />
        {previewKeywords.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {previewKeywords.map((kw) => (
              <span
                key={kw}
                className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-violet-500/15 border border-violet-500/30 text-violet-300"
              >
                {kw}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between">
          <p className="text-[9px] font-mono text-gray-600">
            策略基于内置平台特性库 + 关键词匹配生成，非真实 AI 调用
          </p>
          <button
            onClick={handleGenerate}
            disabled={!topic.trim() || generating}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {generating ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            <span className="text-[9px] font-mono uppercase tracking-wider">
              {generating ? "生成中..." : "生成策略"}
            </span>
          </button>
        </div>
      </div>

      {/* 策略展示区域 */}
      {activeStrategy ? (
        <StrategyView strategy={activeStrategy} />
      ) : (
        <div className="py-12 text-center">
          <Layers size={22} className="mx-auto text-gray-600 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">输入选题生成跨平台内容策略</p>
        </div>
      )}

      {/* 历史策略列表 */}
      {recentStrategies.length > 0 && (
        <div className="p-4 border-t border-white/[0.04]">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={11} className="text-gray-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              历史策略（最近 {recentStrategies.length} 条）
            </span>
          </div>
          <div className="space-y-1.5">
            {recentStrategies.map((s) => {
              const isActive = s.id === activeId;
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition-colors ${
                    isActive
                      ? "border-cyan-500/30 bg-cyan-500/[0.06]"
                      : "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-gray-200 truncate">{s.topic}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] font-mono text-gray-500">{s.createdAt}</span>
                      {s.keywords.length > 0 && (
                        <>
                          <span className="text-[9px] font-mono text-gray-600">·</span>
                          <span className="text-[9px] font-mono text-violet-400/70 truncate">
                            {s.keywords.slice(0, 3).join("/")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      onClick={() => handleView(s.id)}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-400 hover:text-cyan-300 transition-colors"
                    >
                      <Eye size={10} />
                      查看
                    </button>
                    <button
                      onClick={() => handleDelete(s.id)}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={10} />
                      删除
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 生成逻辑说明 */}
      <div className="px-4 pb-4">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Lightbulb size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">生成逻辑说明</span>
          </div>
          <p className="text-[10px] font-mono text-gray-500 leading-relaxed">
            本引擎的策略基于内置平台特性库（PLATFORM_PROFILES）与关键词亲和度规则（KEYWORD_AFFINITY_RULES）计算生成，
            非真实 AI 调用。每平台根据选题关键词命中规则累加亲和度得分（基准 25 分），映射为主推/辅助/补充优先级；
            联动模板基于选题语义匹配推荐；内容矩阵按各平台最佳发布时机排期。所有依据均为内置知识，可追溯可解释。
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 策略展示子组件
// ============================================

interface StrategyViewProps {
  strategy: CrossStrategy;
}

function StrategyView({ strategy }: StrategyViewProps) {
  // 按亲和度降序排列
  const sortedStrategies = useMemo(
    () => [...strategy.platformStrategies].sort((a, b) => b.affinityScore - a.affinityScore),
    [strategy.platformStrategies]
  );

  const recommendedLinkage = useMemo(
    () => LINKAGE_TEMPLATES.find((t) => t.id === strategy.recommendedLinkageId) || LINKAGE_TEMPLATES[0],
    [strategy.recommendedLinkageId]
  );

  return (
    <div className="border-b border-white/[0.04]">
      {/* 当前选题信息 */}
      <div className="px-4 py-2.5 bg-white/[0.01] border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <Target size={11} className="text-cyan-400 flex-shrink-0" />
          <span className="text-[11px] text-gray-200 font-mono truncate">选题：{strategy.topic}</span>
          <span className="text-[9px] font-mono text-gray-500 flex-shrink-0 ml-auto">{strategy.createdAt}</span>
        </div>
        {strategy.keywords.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            <span className="text-[9px] font-mono text-gray-500">关键词：</span>
            {strategy.keywords.map((kw) => (
              <span
                key={kw}
                className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-violet-500/15 border border-violet-500/30 text-violet-300"
              >
                {kw}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 平台策略卡片网格 */}
      <div className="p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2 mb-2.5">
          <Layers size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
            6 平台策略建议（按亲和度排序）
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {sortedStrategies.map((ps, idx) => (
            <PlatformStrategyCard key={ps.platform} strategy={ps} index={idx} />
          ))}
        </div>
      </div>

      {/* 跨平台联动策略 */}
      <div className="p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2 mb-2.5">
          <Route size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">跨平台联动策略建议</span>
        </div>
        {/* 推荐联动流程图 */}
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-4 mb-2.5">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles size={10} className="text-emerald-400" />
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">推荐方案</span>
            <span className="text-[11px] font-mono text-gray-200">{recommendedLinkage.name}</span>
          </div>
          <LinkageFlow template={recommendedLinkage} highlighted />
          <p className="text-[10px] font-mono text-gray-500 mt-2 leading-relaxed">{recommendedLinkage.description}</p>
        </div>
        {/* 全部联动模板 */}
        <div className="space-y-1.5">
          {LINKAGE_TEMPLATES.map((tpl) => (
            <div
              key={tpl.id}
              className={`rounded-md border px-2.5 py-2 ${
                tpl.id === strategy.recommendedLinkageId
                  ? "border-emerald-500/20 bg-emerald-500/[0.02]"
                  : "border-white/[0.04] bg-white/[0.02]"
              }`}
            >
              <LinkageFlow template={tpl} compact />
              <p className="text-[9px] font-mono text-gray-500 mt-1">{tpl.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 内容矩阵规划 */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Calendar size={11} className="text-violet-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">内容矩阵规划</span>
        </div>
        {/* 一周排期 */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 mb-2.5">
          <div className="text-[10px] font-mono text-violet-300 uppercase tracking-wider mb-2">一周内容排期</div>
          <div className="space-y-1">
            {strategy.weeklyMatrix.map((item) => {
              const meta = PLATFORM_META[item.platform];
              const Icon = meta.icon;
              return (
                <div key={item.day} className="flex items-center gap-2">
                  <span className="w-10 text-[10px] font-mono text-gray-400 flex-shrink-0">{item.day}</span>
                  <Icon size={11} style={{ color: meta.color }} className="flex-shrink-0" />
                  <span
                    className="px-1 rounded text-[9px] font-mono flex-shrink-0"
                    style={{ background: `${meta.color}20`, color: meta.color }}
                  >
                    {PLATFORM_PROFILES[item.platform].label}
                  </span>
                  <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{item.content}</span>
                </div>
              );
            })}
          </div>
        </div>
        {/* 一个月主题规划 */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="text-[10px] font-mono text-violet-300 uppercase tracking-wider mb-2">一个月主题规划</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {strategy.monthlyPlan.map((plan) => (
              <div key={plan.week} className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-mono text-violet-300">{plan.week}</span>
                  <span className="text-[10px] font-mono text-gray-300 truncate flex-1">{plan.theme}</span>
                </div>
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  {plan.focusPlatforms.map((p) => {
                    const meta = PLATFORM_META[p];
                    return (
                      <span
                        key={p}
                        className="px-1 rounded text-[8px] font-mono"
                        style={{ background: `${meta.color}20`, color: meta.color }}
                      >
                        {PLATFORM_PROFILES[p].label}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 平台策略卡片
// ============================================

interface PlatformStrategyCardProps {
  strategy: PlatformStrategy;
  index: number;
}

function PlatformStrategyCard({ strategy, index }: PlatformStrategyCardProps) {
  const meta = PLATFORM_META[strategy.platform];
  const Icon = meta.icon;
  const FormatIcon = meta.formatIcon;
  const priority = PRIORITY_META[strategy.priority];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4"
      style={{ borderTop: `2px solid ${meta.color}` }}
    >
      {/* 平台名 + 优先级 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={13} style={{ color: meta.color }} />
          <span className="text-[12px] font-semibold text-gray-200">{strategy.profile.label}</span>
        </div>
        <div className="flex items-center gap-1">
          <span
            className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider"
            style={{ background: `${priority.color}20`, color: priority.color }}
          >
            {priority.label}
          </span>
        </div>
      </div>

      {/* 亲和度得分 */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-[9px] font-mono text-gray-500">亲和度</span>
        <div className="flex-1 h-1.5 rounded bg-white/[0.04] overflow-hidden">
          <div
            className="h-full rounded"
            style={{ width: `${strategy.affinityScore}%`, background: `linear-gradient(90deg, ${meta.color}80, ${meta.color})` }}
          />
        </div>
        <span className="text-[9px] font-mono w-6 text-right" style={{ color: meta.color }}>
          {strategy.affinityScore}
        </span>
      </div>

      {/* 选题角度建议 */}
      <div className="rounded-md bg-white/[0.02] border border-white/[0.04] px-2 py-1.5 mb-2">
        <div className="flex items-center gap-1 mb-0.5">
          <Lightbulb size={9} className="text-amber-400 flex-shrink-0" />
          <span className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">选题角度</span>
        </div>
        <p className="text-[10px] font-mono text-gray-300 leading-snug">{strategy.topicAngle}</p>
      </div>

      {/* 详情字段 */}
      <div className="space-y-1">
        <StrategyField icon={FormatIcon} iconColor={meta.color} label="内容形式" value={strategy.profile.bestFormat} />
        <StrategyField label="切入角度" value={strategy.profile.bestAngle} />
        <StrategyField label="风格建议" value={strategy.profile.bestStyle} />
        <StrategyField icon={Clock} iconColor="#FBBF24" label="发布时间" value={strategy.profile.bestTiming} />
        <StrategyField icon={Target} iconColor="#34D399" label="角色定位" value={strategy.profile.role} />
        <StrategyField icon={TrendingUp} iconColor="#38BDF8" label="平台优势" value={strategy.profile.strength} />
      </div>
    </motion.div>
  );
}

// ============================================
// 策略字段行
// ============================================

interface StrategyFieldProps {
  label: string;
  value: string;
  icon?: typeof FileText;
  iconColor?: string;
}

function StrategyField({ label, value, icon: Icon, iconColor }: StrategyFieldProps) {
  return (
    <div className="flex items-start gap-1.5">
      <div className="flex items-center gap-1 w-16 flex-shrink-0">
        {Icon && <Icon size={9} style={{ color: iconColor || "#6B7280" }} className="flex-shrink-0" />}
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <span className="text-[10px] font-mono text-gray-300 leading-snug flex-1">{value}</span>
    </div>
  );
}

// ============================================
// 联动流程图
// ============================================

interface LinkageFlowProps {
  template: LinkageTemplate;
  highlighted?: boolean;
  compact?: boolean;
}

function LinkageFlow({ template, highlighted, compact }: LinkageFlowProps) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {template.flow.map((node, idx) => {
        const isPrivate = node === "私域";
        const platform = isPrivate ? null : (node as MarketingPlatform);
        const meta = platform ? PLATFORM_META[platform] : null;
        const label = isPrivate ? "私域" : platform ? PLATFORM_PROFILES[platform].label : node;
        const color = meta ? meta.color : "#A78BFA";
        const Icon = meta ? meta.icon : Layers;
        return (
          <div key={`${node}-${idx}`} className="flex items-center gap-1">
            {idx > 0 && <ArrowRight size={compact ? 9 : 11} className="text-gray-600" />}
            <div
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${
                compact ? "text-[9px]" : "text-[10px]"
              } font-mono`}
              style={{
                background: `${color}15`,
                border: `1px solid ${highlighted ? `${color}50` : `${color}25`}`,
                color,
              }}
            >
              <Icon size={compact ? 9 : 10} />
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
